import moment from "moment/moment";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Col, Container, Row } from "reactstrap";

import { adminStatsEndpoints } from "../../../../../api";
import KpiTile from "../../../../../components/admin/KpiTile";
import PageHeader from "../../../../../components/admin/PageHeader";
import MonthlyPayoutsGraphCard from "../../../../../components/adminStats/MonthlyPayoutsGraphCard";
import usePayouts from "../../../../../components/payouts/hooks/usePayouts";
import PayoutsTableCard from "../../../../../components/payouts/PayoutsTableCard";
import useFetch from "../../../../../hooks/useFetch";

const eur = (n: number) => `${n.toFixed(2)} €`;

const AffiliatesPayouts: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "adminPages.payouts" });
  const now = moment();

  const {
    claims,
    isLoading: isLoadingPayouts,
    pagination,
  } = usePayouts({
    sort: { createdAt: "down" },
    filters: { showFailed: false },
    pagination: { pageSize: 10 },
  });

  const [year, setYear] = useState(now.format("YYYY"));
  const { state: yearsPayoutStats, isLoading: isLoadingStats } = useFetch(
    () => adminStatsEndpoints.getYearlyMonthlyPayouts().then(({ data }) => data),
    { cacheKey: "yearly_monthly_payouts" }
  );
  const yearMonthlyPayoutStats = useMemo(() => yearsPayoutStats?.[year], [year, yearsPayoutStats]);

  const currMonthPaidOut = useMemo(
    () => yearsPayoutStats?.[now.format("YYYY")]?.perMonth?.[now.format("M")]?.total?.EUR || 0,
    [now, yearsPayoutStats]
  );
  const totalPaidOut = useMemo(
    () => (yearsPayoutStats ? Object.values(yearsPayoutStats).reduce((r, y) => r + (y?.total?.EUR || 0), 0) : 0),
    [yearsPayoutStats]
  );

  return (
    <Container className="mt-4" fluid>
      <PageHeader title={t("title")} subtitle={t("subtitle")} />
      <Row className="mb-3" style={{ rowGap: 12 }}>
        <Col xs={12} sm={6} xl={3}>
          <KpiTile
            label={t("totalPaidOut")}
            value={eur(totalPaidOut)}
            detail={t("totalPaidOutDescription")}
            icon="fas fa-coins"
            isLoading={isLoadingStats}
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <KpiTile
            label={t("paidOutThisMonth", { month: now.format("MMMM") })}
            value={eur(currMonthPaidOut)}
            tone="ok"
            detail={t("paidOutThisMonthDescription")}
            icon="fas fa-calendar-check"
            isLoading={isLoadingStats}
          />
        </Col>
        <Col xs={12} sm={6} xl={3}>
          <KpiTile
            label={t("totalPayouts")}
            value={pagination?.total ?? 0}
            detail={t("totalDescription")}
            icon="fas fa-hand-holding-usd"
            isLoading={isLoadingPayouts && !pagination?.total}
          />
        </Col>
      </Row>
      <Row style={{ rowGap: 16 }}>
        <Col xs={12}>
          <PayoutsTableCard
            claims={claims}
            isLoading={isLoadingPayouts}
            filtersToShow={["user", "failed", "downloadSelected"]}
            columnsToShow={["user"]}
          />
        </Col>
        <Col xs={12}>
          <MonthlyPayoutsGraphCard
            year={year}
            setYear={setYear}
            data={yearMonthlyPayoutStats}
            isLoading={isLoadingStats}
          />
        </Col>
      </Row>
    </Container>
  );
};

export default AffiliatesPayouts;
